import React from 'react';
import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { CashCount, CashCountStatus } from '../../../types/finance';
import { formatPaisa } from '../../../utils/paisa';
import { ChipTone, ToneChip } from '../../common/StatusChip';

const STATUS: Record<CashCountStatus, { label: string; tone: ChipTone }> = {
  AGREED: { label: 'Agreed', tone: 'success' },
  PENDING: { label: 'Awaiting approval', tone: 'warning' },
  POSTED: { label: 'Difference posted', tone: 'info' },
  REJECTED: { label: 'Sent back', tone: 'neutral' },
};

export const CASH_COUNT_STATUS_OPTIONS = (Object.keys(STATUS) as CashCountStatus[]).map(value => ({ value, label: STATUS[value].label }));

/** A count's state; a sent-back count shows why on hover. */
export const CashCountStatusChip: React.FC<{ count: Pick<CashCount, 'status' | 'rejectionReason' | 'decidedByName'> }> = ({ count }) => {
  const chip = <ToneChip label={STATUS[count.status].label} tone={STATUS[count.status].tone} />;
  return count.status === 'REJECTED' && count.rejectionReason
    ? <Tooltip title={`${count.decidedByName ?? 'Someone'}: ${count.rejectionReason}`}><span>{chip}</span></Tooltip>
    : chip;
};

/** Counted against expected: "Agrees", "Over 250.00" (green) or "Short 250.00" (red). */
export const CashDifference: React.FC<{ paisa: number } & Omit<TypographyProps, 'children'>> = ({ paisa, sx, ...rest }) => (
  <Typography
    variant="body2"
    component="span"
    fontWeight={600}
    color={paisa === 0 ? 'success.dark' : paisa > 0 ? 'success.main' : 'error.main'}
    sx={[{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }, ...(Array.isArray(sx) ? sx : [sx])]}
    {...rest}
  >
    {paisa === 0 ? 'Agrees' : `${paisa > 0 ? 'Over' : 'Short'} ${formatPaisa(Math.abs(paisa))}`}
  </Typography>
);
